import 'maplibre-gl/dist/maplibre-gl.css'

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  APPROXIMATE_RADIUS_M,
  circlePolygon,
} from '@/features/map/privacy'
import { loadMaplibre } from '@/features/map/maplibre-loader'
import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'

interface ActivityMapPreviewProps {
  latitude: number
  longitude: number
  exact: boolean
  areaLabel: string
  color?: string | null
  onOpenDirections?: () => void
}

type MapStatus = 'fallback' | 'loading' | 'ready'

const OPEN_FREE_MAP_STYLE = 'https://tiles.openfreemap.org/styles/bright'
const DEFAULT_AREA_COLOR = '#00645f'

function supportsWebgl(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof WebGLRenderingContext !== 'undefined'
  )
}

/**
 * Read-only map preview. Creator/confirmed viewers see the exact pin and can
 * open directions from it. Everyone else sees only the 300 m privacy area
 * (D-085): no pin, because a marker would imply a precision the API does not
 * expose. If WebGL is unavailable (tests, unsupported WebViews, privacy modes)
 * the brand-token fallback keeps the location context without a network
 * request.
 */
export function ActivityMapPreview({
  latitude,
  longitude,
  exact,
  areaLabel,
  color,
  onOpenDirections,
}: ActivityMapPreviewProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const directionsRef = useRef(onOpenDirections)
  const [status, setStatus] = useState<MapStatus>('fallback')
  const areaColor = color || DEFAULT_AREA_COLOR
  const directionsLabel = t('map.directions')

  useEffect(() => {
    directionsRef.current = onOpenDirections
  }, [onOpenDirections])

  useEffect(() => {
    if (!supportsWebgl() || !containerRef.current) return

    let disposed = false
    let map: import('maplibre-gl').Map | null = null
    let resizeObserver: ResizeObserver | null = null
    setStatus('loading')

    void loadMaplibre()
      .then(({ Map, Marker }) => {
        if (disposed || !containerRef.current) return
        const instance = new Map({
          container: containerRef.current,
          style: OPEN_FREE_MAP_STYLE,
          center: [longitude, latitude],
          zoom: exact ? 15 : 14,
          interactive: false,
          attributionControl: false,
          fadeDuration: 0,
        })
        map = instance
        instance.on('load', () => {
          if (disposed) return
          // The surrounding detail grid can settle after the map is created;
          // make sure the canvas takes the final card width.
          instance.resize()
          window.requestAnimationFrame(() => {
            if (!disposed) instance.resize()
          })
          if (exact) {
            const marker = new Marker({
              color: '#00645F',
              scale: 1,
            })
              .setLngLat([longitude, latitude])
              .addTo(instance)
            // The visible pin is a shortcut; the sr-only button below keeps
            // the action keyboard/screen-reader accessible.
            if (directionsRef.current) {
              const element = marker.getElement()
              element.style.cursor = 'pointer'
              element.title = directionsLabel
              element.addEventListener('click', () => {
                directionsRef.current?.()
              })
            }
          } else {
            instance.addSource('activity-area', {
              type: 'geojson',
              data: circlePolygon(
                latitude,
                longitude,
                APPROXIMATE_RADIUS_M,
                areaColor,
              ),
            })
            instance.addLayer({
              id: 'activity-area',
              type: 'fill',
              source: 'activity-area',
              paint: {
                'fill-color': ['get', 'color'],
                'fill-opacity': 0.16,
              },
            })
          }
          setStatus('ready')
        })
        instance.on('error', () => {
          if (!disposed) setStatus('fallback')
        })
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => {
            if (!disposed) instance.resize()
          })
          resizeObserver.observe(containerRef.current)
        }
      })
      .catch(() => {
        if (!disposed) setStatus('fallback')
      })

    return () => {
      disposed = true
      resizeObserver?.disconnect()
      resizeObserver = null
      map?.remove()
      map = null
    }
  }, [areaColor, directionsLabel, exact, latitude, longitude])

  return (
    <div className="activity-map-preview relative h-[180px] overflow-hidden rounded-lg border border-line bg-mint">
      <div
        ref={containerRef}
        role="img"
        aria-label={t('activityDetail.map.label', {
          area: areaLabel,
        })}
        className="activity-map-preview__canvas h-full w-full overflow-hidden rounded-[inherit]"
      />

      {status !== 'ready' ? (
        <div
          className="absolute inset-0 grid place-items-center bg-mint"
          aria-hidden="true"
        >
          <span
            className={
              exact
                ? 'grid size-14 place-items-center rounded-full bg-white text-teal shadow-md'
                : 'grid size-28 rounded-full border-2 border-dashed border-lilla/70 bg-lilla/10'
            }
          >
            {exact ? <MapPin size={30} weight={kuniriIconWeight} /> : null}
          </span>
        </div>
      ) : null}

      {status === 'loading' ? (
        <span
          className="spinner absolute top-3 right-3 bg-white/80"
          aria-hidden="true"
        />
      ) : null}

      {exact && onOpenDirections ? (
        <button
          type="button"
          onClick={onOpenDirections}
          className="sr-only"
        >
          {directionsLabel}
        </button>
      ) : null}

      <a
        href="https://www.openstreetmap.org/copyright"
        target="_blank"
        rel="noreferrer"
        className="absolute bottom-2 left-2 rounded-sm bg-white/90 px-2 py-1 text-[11px] font-semibold text-muted hover:text-teal"
      >
        {t('activityDetail.map.attribution')}
      </a>
    </div>
  )
}

const MapPin = KuniriIcons.mapPin
